import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Account } from '../domain/account';
import { useFetcher } from '../../graphql/hooks/useFetcher';
import { NotificationPreferenceFields } from '../../preferences/notificationPreferenceFields';
import { useAuthentication } from './useAuthentication';

export type useCurrentAccountHook = () => Account | null;

export const useCurrentAccount = () => {
  const authentication = useAuthentication();
  const keyCloakId = authentication?.jwt?.sub ?? null;
  const cacheTime = 4 * 3600 * 1000; // 4 hours

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['currentAccount'],
    queryFn: useFetcher(LOAD_CURRENT_ACCOUNT).bind(null, { keyCloakId }),
    enabled: keyCloakId !== null,
    gcTime: cacheTime,
    staleTime: cacheTime,
  });

  const account = useMemo(() => (data ? getAccount(data) : null), [data]);

  return { account, isLoading, isFetching };
};

const getAccount = (data: any) => {
  const record = data.findAccounts.items[0];
  return new Account(record);
};

const LOAD_CURRENT_ACCOUNT = `
  query LoadAccount ( $keyCloakId: String! ) {
    findAccounts( filter: {
      keyCloakId: { eq: $keyCloakId }
    }) {
      items {
        id
        allowedWorkspaces {
          id
          name
          type
        }
        removedWorkspaces {
          id
          name
          type
        }
        roles {
          id
          permissions
          name
        }
        name {
          first
          last
        }
        avatar {
          id
          type
          extension
        }
        email
        phone {
          callingCode
          countryCode
          phoneNumber
        }
        jobTitle
        workspace {
          name
          id
          type
        }
        groups {
          id
        }
        preferences {
          preferredLanguage
          theme
          # Select every notificationPreferences field, never a subset. Callers
          # that persist a preference (theme, preferred language) send back a
          # spread of account.preferences, and graphback's updateAccount $sets
          # the whole preferences subdocument: any field missing from this
          # selection is erased on the next write.
          ${NotificationPreferenceFields}
        }
        workspaceRoles {
          workspace
          role
        }
      }
    }
  }
`;
